import type BABYLON from 'babylonjs';

import type { Globals } from './globals';
import { compactOptions } from './options';

// cf. https://doc.babylonjs.com/divingDeeper/postProcesses/defaultRenderingPipeline

function getDefaultRenderingPipeline(
  globals: Globals,
  scene: BABYLON.Scene,
  name: string
): BABYLON.DefaultRenderingPipeline | undefined {
  return scene.postProcessRenderPipelineManager.supportedPipelines
    .filter(
      (p): p is BABYLON.DefaultRenderingPipeline =>
        p instanceof globals.BABYLON.DefaultRenderingPipeline
    )
    .find((p) => p.name === name);
}

export interface DefaultRenderingPipelineOptions {
  imageProcessingEnabled?: BABYLON.DefaultRenderingPipeline['imageProcessingEnabled'];
  imageProcessingExposure?: BABYLON.DefaultRenderingPipeline['imageProcessing']['exposure'];
  samples?: BABYLON.DefaultRenderingPipeline['samples'];
  fxaaEnabled?: BABYLON.DefaultRenderingPipeline['fxaaEnabled'];
  sharpenEnabled?: BABYLON.DefaultRenderingPipeline['sharpenEnabled'];
  sharpenEdgeAmount?: BABYLON.DefaultRenderingPipeline['sharpen']['edgeAmount'];
}

export function createOrUpdateDefaultRenderingPipeline(
  globals: Globals,
  scene: BABYLON.Scene,
  name: string,
  options: DefaultRenderingPipelineOptions | null = null
): BABYLON.DefaultRenderingPipeline | null {
  if (!options) {
    getDefaultRenderingPipeline(globals, scene, name)?.dispose();
    return null;
  }

  const pipeline =
    getDefaultRenderingPipeline(globals, scene, name) ||
    new globals.BABYLON.DefaultRenderingPipeline(name, true, scene, scene.cameras);
  const { imageProcessingExposure, ...pipelineOptions } = options;
  Object.assign(pipeline, compactOptions(pipelineOptions));
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (pipeline.imageProcessing) {
    Object.assign(pipeline.imageProcessing, compactOptions({ exposure: imageProcessingExposure }));
  }
  return pipeline;
}
